import IconTile from "@/components/common/iconTile/iconTile.component";
import RingBulletItem from "@/components/common/ringBulletItem/ringBulletItem.component";
import { cn } from "@/lib/utils";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import LinkedText from "../linkedText/linkedText.component";
import type { CardGridItem } from "./cardGrid.type";

interface CardGridProps {
  cards: CardGridItem[];
  columns?: 2 | 3 | 4;
}

// Mobile-first column counts: one column on phones, two from `sm`, then
// the full count from `lg` (the preview's `.cardgrid.c2/.c3/.c4`).
const columnClass = {
  2: "sm:grid-cols-2",
  3: "sm:grid-cols-2 lg:grid-cols-3",
  4: "sm:grid-cols-2 lg:grid-cols-4",
};

// The soft circle peeking in at each card's top-right corner, cycled so
// neighbouring cards differ.
const circleTones = [
  "bg-tone-peach",
  "bg-tone-mint",
  "bg-tone-lilac",
  "bg-tone-sun",
  "bg-tone-sky",
  "bg-tone-blush",
];

// Grid of white cards: optional icon tile, title, text, optional ring-bullet
// list and fine print, and an optional link at the foot. The card lifts and
// its corner circle grows on hover.
const CardGrid = ({ cards, columns = 3 }: CardGridProps) => (
  <div className={cn("grid gap-6", columnClass[columns])}>
    {cards.map((card, index) => (
      <article
        key={card.title}
        id={card.id}
        className="group relative flex scroll-mt-[88px] flex-col gap-2.5 overflow-hidden rounded-[28px] bg-white p-6 shadow-card transition-[transform,box-shadow] duration-400 ease-[cubic-bezier(.2,.8,.2,1)] hover:-translate-y-2 hover:shadow-lift motion-reduce:transition-none motion-reduce:hover:translate-y-0 sm:p-[30px]"
      >
        <span
          aria-hidden="true"
          className={cn(
            "absolute -top-[46px] -right-[46px] size-[150px] rounded-full transition-transform duration-600 ease-[cubic-bezier(.2,.8,.2,1)] group-hover:scale-[1.7] motion-reduce:transition-none motion-reduce:group-hover:scale-100",
            circleTones[index % circleTones.length],
          )}
        />
        {card.icon && (
          <span className="relative mb-1.5 self-start rounded-[14px] bg-white shadow-phone">
            <IconTile icon={card.icon} index={index} size="lg" />
          </span>
        )}
        <h3 className="relative text-[1.2rem] leading-snug font-bold tracking-[-0.01em]">
          {card.title}
        </h3>
        <p className="relative text-[15.5px] leading-relaxed text-ink-muted">
          <LinkedText text={card.text} />
        </p>
        {card.items && card.items.length > 0 && (
          <ul className="relative grid gap-2">
            {card.items.map((item) => (
              <RingBulletItem
                key={item}
                className="text-[15px] leading-snug text-ink-muted"
              >
                {item}
              </RingBulletItem>
            ))}
          </ul>
        )}
        {card.fine && (
          <p className="relative mt-1 text-sm leading-normal text-ink-muted">
            {card.fine}
          </p>
        )}
        {card.link && (
          <Link
            href={card.link.route}
            className="relative mt-auto inline-flex items-center gap-2 self-start pt-2 text-[15px] font-bold text-primary hover:text-ink"
          >
            {card.link.label}
            <ArrowRight
              className="size-4 transition-transform duration-300 group-hover:translate-x-1.5 motion-reduce:transition-none"
              aria-hidden="true"
            />
          </Link>
        )}
      </article>
    ))}
  </div>
);

export default CardGrid;
